import React, { useState, useEffect, useContext, useLayoutEffect, useMemo, useCallback } from 'react';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  Globe,
  Award,
  Edit2,
  Save,
  X,
  Camera,
  CheckCircle,
  AlertCircle,
  GraduationCap,
  Link as LinkIcon,
  Twitter,
  Linkedin,
  Github,
  FileText,
  Loader2
} from 'lucide-react';
import './Profile.css';
import { ApiUrl, BaseUrl } from '../API/ApiUrl';
import { ProfileContext } from '../Context/ProfileContext';
import Toast from "../components/Toast/Toast";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const { triggerProfileRefresh } = useContext(ProfileContext);

  const [profileData, setProfileData] = useState({});

  const [formData, setFormData] = useState({});
  const [newEducation, setNewEducation] = useState('');
  const [newExpertise, setNewExpertise] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success"
  });


  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "name") {
      if (/[^A-Za-z\s]/.test(value)) {
        setToast({
          show: true,
          message: "Name should contain only letters",
          type: "error"
        });
      }

      const cleaned = value.replace(/[^A-Za-z\s]/g, "");

      setFormData({
        ...formData,
        name: cleaned
      });
      return;
    }

    if (name === "phone") {
      let cleaned = value.replace(/[^0-9]/g, "");

      if (cleaned.length > 10) {
        cleaned = cleaned.slice(0, 10);
      }

      if (cleaned.length === 1 && !/[6-9]/.test(cleaned)) {
        setToast({
          show: true,
          message: "Phone number should start with 6-9",
          type: "error"
        });
        return;
      }

      setFormData({
        ...formData,
        phone: cleaned
      });

      return;
    }

    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };
  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log("Profile file selected:", file);

    setImageFile(file);   // store file separately
    setProfileImage(URL.createObjectURL(file)); // preview
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log("Cover file selected:", file);

    setCoverFile(file);   // store file separately
    setCoverImage(URL.createObjectURL(file)); // preview
  };

  const handleRemoveProfileImage = () => {
    setProfileImage(null);

    setFormData((prev) => ({
      ...prev,
      profile_image: null,
      remove_profile: true
    }));
  };

  const handleRemoveCoverImage = () => {
    setCoverImage(null);

    setFormData((prev) => ({
      ...prev,
      cover_image: null,
      remove_cover: true
    }));
  };



  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem("adminToken");

      const response = await fetch(`${ApiUrl}/admin-profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      // console.log("Profile_data", data)

      setProfileData(data);
      setFormData(data);

      if (data.profile_image) {
        const profilePath = `${BaseUrl}/uploads/${data.profile_image}`;
        setProfileImage(profilePath);
      }

      if (data.cover_image) {
        const coverPath = `${BaseUrl}/uploads/${data.cover_image}`;
        setCoverImage(coverPath);
      }


    } catch (error) {
      console.error("Profile fetch error:", error);
    }
  }, []);


  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);


  const userDisplayName = useMemo(() => {
    if (!profileData.name) return "Admin";
    return profileData.name;
  }, [profileData.name]);

  const userEmail = useMemo(() => {
    if (!profileData.email) return "No email";
    return profileData.email;
  }, [profileData.email]);

  const userLocation = useMemo(() => {
    if (!profileData.location) return "Not specified";
    return profileData.location;
  }, [profileData.location]);

  const userPosition = useMemo(() => {
    if (!profileData.position) return "Not specified";
    return profileData.position;
  }, [profileData.position]);

  const userPhone = useMemo(() => {
    if (!profileData.phone) return "No phone";
    return profileData.phone;
  }, [profileData.phone]);


  const validateForm = () => {
    const name = formData.name?.trim();
    const email = formData.email?.trim();
    const phone = formData.phone?.trim();

    // Required
    if (!name || !email) {
      setToast({
        show: true,
        message: "Name and Email are required",
        type: "error"
      });
      return false;
    }

    // Name validation
    if (!/^[A-Za-z\s]+$/.test(name)) {
      setToast({
        show: true,
        message: "Name should contain only letters",
        type: "error"
      });
      return false;
    }

    // Phone validation
    if (phone && !/^\d+$/.test(phone)) {
      setToast({
        show: true,
        message: "Phone number should contain only numbers",
        type: "error"
      });
      return false;
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|in)$/;

    if (!emailRegex.test(email)) {
      setToast({
        show: true,
        message: "Enter a valid email",
        type: "error"
      });
      return false;
    }

    return true;
  };


  const handleSave = async () => {
    console.log("Saving");
    if (!validateForm()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");

      const form = new FormData();

      form.append("name", formData.name || "");
      form.append("email", formData.email || "");
      form.append("phone", formData.phone || "");
      form.append("location", formData.location || "");
      form.append("position", formData.position || "");

      form.append("remove_profile", formData.remove_profile || false);
      form.append("remove_cover", formData.remove_cover || false);

      if (imageFile) {
        form.append("profile_image", imageFile);
      }

      if (coverFile) {
        form.append("cover_image", coverFile);
      }

      console.log("FormData being sent:");
      for (let pair of form.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await fetch(`${ApiUrl}/admin-profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: form,
      });

      const data = await response.json();

      console.log("Server response:", data);

      if (response.ok) {
        setProfileData(data);
        setIsEditing(false);
        fetchProfile();
        setSuccessMessage("Profile updated successfully");
        setTimeout(() => setSuccessMessage(""), 3000);
        // Trigger profile refresh in AdminHeader
        triggerProfileRefresh();
      } else {
        setErrorMessage(data.message || "Failed to update profile");
      }

    } catch (error) {
      console.error("Save error:", error);
      setErrorMessage("Server error");
    }
    setSaving(false);

  };


  const handleCancel = useCallback(() => {
    setFormData({ ...profileData });

    if (profileData?.profile_image) {
      setProfileImage(`${BaseUrl}/uploads/${profileData.profile_image}`);
    } else {
      setProfileImage(null);
    }

    if (profileData?.cover_image) {
      setCoverImage(`${BaseUrl}/uploads/${profileData.cover_image}`);
    } else {
      setCoverImage(null);
    }

    setIsEditing(false);
    setErrorMessage('');
    setNewEducation('');
    setNewExpertise('');
    setNewLanguage('');
  }, [profileData])



  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-header">
        <div className="title-section">
          <div className="title-icon">
            <User size={24} />
          </div>
          <div>
            <h1 className="page-title">My Profile</h1>
            <p className="page-subtitle">Manage your personal information and professional details</p>
          </div>
        </div>


      </div>

      {/* Messages */}
      {successMessage && (
        <div className="alert success">
          <CheckCircle size={18} />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="alert error">
          <AlertCircle size={18} />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="cover-section">
        <div className="cover-image-container">
          {coverImage ? (
            <img loading='lazy' src={coverImage} alt="Cover" className="cover-image" />
          ) : (
            <div className="cover-gradient"></div>
          )}
          {isEditing && (
            <div className="cover-edit-overlay">
              <label htmlFor="cover-image" className="cover-edit-btn">
                <Camera size={18} />
                <span>Change Cover</span>
              </label>
              <input
                type="file"
                id="cover-image"
                accept="image/*"
                name='cover_image'
                onChange={handleCoverImageChange}
                style={{ display: 'none' }}
              />
              {/* {coverImage !== 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&h=300&q=80' && ( */}
              <button className="cover-remove-btn" onClick={handleRemoveCoverImage}>
                <X size={16} />
              </button>
              {/* )} */}
            </div>
          )}
        </div>

        {/* Profile Image */}
        <div className="profile-image-wrapper">
          <div className="profile-image-container">
            {profileImage ? (
              <img loading='lazy' src={profileImage} alt="Profile" className="profile-image" />
            ) : (
              <div className="profile-placeholder">
                <User size={40} />
              </div>
            )}

            {isEditing && (
              <div className="profile-image-edit">
                <label htmlFor="profile-image" className="image-edit-btn">
                  <Camera size={16} />
                </label>

                <input
                  type="file"
                  id="profile-image"
                  accept="image/*"
                  name='profile_image'
                  onChange={handleProfileImageChange}
                  style={{ display: "none" }}
                />

                {profileImage && (
                  <button
                    className="image-remove-btn"
                    onClick={handleRemoveProfileImage}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>


      <div className="info-card">
        <h3>Basic Information</h3>
        <div className="info-grid">
          <div className="info-row">
            <div className="info-label">
              <User size={16} />
              <span>Name</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="edit-input"
              />
            ) : (
              <div className="info-value">{userDisplayName}</div>
            )}
          </div>


          <div className="info-row">
            <div className="info-label">
              <Mail size={16} />
              <span>Email</span>
            </div>
            {isEditing ? (
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="edit-input"
              />
            ) : (
              <div className="info-value">{userEmail}</div>
            )}
          </div>

          <div className="info-row">
            <div className="info-label">
              <Phone size={16} />
              <span>Phone</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="edit-input"
              />
            ) : (
              <div className="info-value">{userPhone}</div>
            )}
          </div>

          <div className="info-row">
            <div className="info-label">
              <MapPin size={16} />
              <span>Location</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="edit-input"
              />
            ) : (
              <div className="info-value">{userLocation}</div>
            )}
          </div>

          <div className="info-row">
            <div className="info-label">
              <User size={16} />
              <span>Position</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                className="edit-input"
              />
            ) : (
              <div className="info-value">{userPosition}</div>
            )}
          </div>

          {/* <div className="info-row">
              <div className="info-label">
                <Calendar size={16} />
                <span>Member Since</span>
              </div>
              <div className="info-value">{formatDate(profileData.memberSince)}</div>
            </div> */}
        </div>

      </div>
      {!isEditing ? (
        <button className="btn-edit" onClick={() => setIsEditing(true)}>
          <Edit2 size={16} />
          <span>Edit Profile</span>
        </button>
      ) : (
        <div className="edit-actions">

          <button className="btn-cancel" onClick={handleCancel} disabled={saving}>
            <X size={16} />
            <span>Cancel</span>
          </button>

          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 size={16} className="spin-icon" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>

        </div>
      )}
      

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

    </div>
    // </div>
  );
};

export default Profile;